import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Monitor, Smartphone, Tablet, MonitorSmartphone, LogOut, ShieldCheck } from "lucide-react";

const iconFor = (platform) => {
  if (["iPhone", "Android"].includes(platform)) return Smartphone;
  if (platform === "iPad") return Tablet;
  if (["Mac", "Windows", "Linux"].includes(platform)) return Monitor;
  return MonitorSmartphone;
};

const when = (iso) => {
  const then = new Date(iso);
  const mins = Math.round((Date.now() - then.getTime()) / 60000);
  if (mins < 2) return "Active now";
  if (mins < 60) return `${mins} min ago`;
  if (mins < 1440) return `${Math.round(mins / 60)}h ago`;
  return then.toLocaleDateString(undefined, { day: "numeric", month: "short" });
};

/** Every device holding a session, so a client can sign out anything they don't recognise. */
export const DeviceSessions = () => {
  const { logout } = useApp();
  const [sessions, setSessions] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/sessions");
      setSessions(data.sessions);
    } catch (e) { console.error("Sessions load failed:", e); }
  };

  useEffect(() => { load(); }, []);

  const signOut = async (s) => {
    setBusy(s.jti);
    try {
      const { data } = await api.post(`/auth/sessions/${s.jti}/revoke`);
      if (data.was_current) {
        toast.success("Signed out of this device");
        logout();
        return;
      }
      toast.success(`Signed out of ${s.device}`);
      load();
    } catch (e) {
      toast.error("Couldn't sign that device out", { description: e.response?.data?.detail });
    } finally { setBusy(null); }
  };

  const signOutOthers = async () => {
    setBusy("others");
    try {
      const { data } = await api.post("/auth/sessions/revoke-others");
      toast.success(data.revoked
        ? `Signed out of ${data.revoked} other device${data.revoked > 1 ? "s" : ""}`
        : "No other devices were signed in");
      load();
    } catch (e) {
      toast.error("Couldn't sign the other devices out");
    } finally { setBusy(null); }
  };

  const others = sessions.filter((s) => !s.current).length;

  return (
    <Card className="p-5 shadow-premium" data-testid="device-sessions-card">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span className="icon-chip bg-emerald-500/12 text-emerald-700">
            <ShieldCheck className="h-4 w-4" />
          </span>
          <div>
            <div className="text-sm font-medium">Signed-in devices</div>
            <div className="text-xs text-muted-foreground">
              Anything you don't recognise, sign it out — it stops that device immediately.
            </div>
          </div>
        </div>
        {others > 0 && (
          <Button variant="outline" size="sm" onClick={signOutOthers} disabled={busy === "others"}
            data-testid="sign-out-other-devices">
            {busy === "others" ? "Signing out…" : "Sign out others"}
          </Button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {sessions.map((s) => {
          const Icon = iconFor(s.platform);
          return (
            <div key={s.jti} data-testid={`session-row-${s.jti}`}
              className="flex items-center gap-3 rounded-xl border border-border/70 bg-secondary/40 p-3">
              <span className="icon-chip bg-background text-muted-foreground shrink-0">
                <Icon className="h-4 w-4" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium truncate">{s.device}</span>
                  {s.current && (
                    <Badge className="text-[10px]" data-testid="session-current-badge">This device</Badge>
                  )}
                  {s.is_new && !s.current && (
                    <Badge variant="secondary" className="text-[10px] border-amber-500/40 text-amber-700"
                      data-testid="session-new-badge">New</Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {when(s.last_seen)} · {s.ip} · signed in with {s.method}
                </div>
              </div>
              <Button variant="ghost" size="sm" className="shrink-0 text-muted-foreground"
                onClick={() => signOut(s)} disabled={busy === s.jti}
                data-testid={`session-signout-${s.jti}`}>
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline ms-1.5">{s.current ? "Sign out" : "Remove"}</span>
              </Button>
            </div>
          );
        })}
        {!sessions.length && (
          <p className="text-xs text-muted-foreground">No other sessions on this account.</p>
        )}
      </div>
      <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground"
        data-testid="new-device-alert-note">
        We email you the moment your account is opened on a device we haven't seen before, so an
        unfamiliar sign-in never goes unnoticed.
      </p>
    </Card>
  );
};
